import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useAutomationTemplateSetStatusMutation } from '../../../../queries/Automation';

interface UseAutomationStatusOptions {
	templateId: string | undefined;
	currentStatus: Api.TemplateStatus | undefined;
	onStatusChangeSuccess?: (newStatus: Api.TemplateStatus) => void;
	onStatusChangeError?: (error: Api.IOperationResultNoValue) => void;
	/** Optional impersonation context for admin operations */
	impersonationContext?: Api.IImpersonationContext;
}

interface UseAutomationStatusReturn {
	toggleStatus: () => Promise<void>;
	setStatus: (status: Api.TemplateStatus) => Promise<void>;
	isUpdatingStatus: boolean;
	isActive: boolean;
}

export function useAutomationStatus({
	templateId,
	currentStatus,
	onStatusChangeSuccess,
	onStatusChangeError,
	impersonationContext,
}: UseAutomationStatusOptions): UseAutomationStatusReturn {
	const updateStatusMutation = useAutomationTemplateSetStatusMutation({
		onSuccess: data => {
			onStatusChangeSuccess?.(data.status);
		},
		onError: error => {
			onStatusChangeError?.(error);
		},
	});

	const setStatus = useCallback(
		async (status: Api.TemplateStatus) => {
			if (!templateId) {
				return;
			}

			await updateStatusMutation.mutateAsync({
				id: templateId,
				status,
				impersonationContext,
			});
		},
		[templateId, updateStatusMutation, impersonationContext]
	);

	const toggleStatus = useCallback(async () => {
		if (!currentStatus) {
			return;
		}

		const newStatus =
			currentStatus === Api.TemplateStatus.Published ? Api.TemplateStatus.Disabled : Api.TemplateStatus.Published;

		await setStatus(newStatus);
	}, [currentStatus, setStatus]);

	const isActive = currentStatus === Api.TemplateStatus.Published;

	return {
		toggleStatus,
		setStatus,
		isUpdatingStatus: updateStatusMutation.isPending,
		isActive,
	};
}
